import React, { useState } from 'react';
import { ClientToken } from '../utils/tokens';
import { IntakeFormData } from '../types/form';
import ProgressBar from './ProgressBar';
import Step1BusinessInfo from './steps/Step1BusinessInfo';
import Step2Services from './steps/Step2Services';
import Step3Brand from './steps/Step3Brand';
import Step4Timeline from './steps/Step4Timeline';
import SuccessScreen from './SuccessScreen';

interface Props {
  clientToken: ClientToken;
}

const STEP_LABELS = ['Business', 'Services', 'Brand', 'Timeline'];

const INITIAL_FORM: IntakeFormData = {
  businessName: '',
  industry: '',
  website: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  location: '',
  services: [],
  projectDescription: '',
  goals: '',
  hasLogo: '',
  brandColors: '',
  brandPersonality: [],
  competitorSites: '',
  inspiration: '',
  timeline: '',
  budget: '',
  additionalNotes: '',
  uploadedFiles: [],
};

const WEB3FORMS_KEY = 'aea2f248-4e9b-4df8-bac7-8898990e705a';

const IntakeForm: React.FC<Props> = ({ clientToken }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<IntakeFormData>(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const updateForm = (updates: Partial<IntakeFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setError('');
  };

  const validateStep = (): string => {
    if (step === 1) {
      if (!formData.businessName.trim()) return 'Please enter your business name.';
      if (!formData.industry) return 'Please select your industry.';
      if (!formData.contactName.trim()) return 'Please enter your name.';
      if (!formData.contactEmail.trim() || !formData.contactEmail.includes('@'))
        return 'Please enter a valid email address.';
    }
    if (step === 2) {
      if (formData.services.length === 0) return 'Please select at least one service.';
      if (!formData.projectDescription.trim()) return 'Please describe your project.';
    }
    if (step === 4) {
      if (!formData.timeline) return 'Please select a timeline.';
      if (!formData.budget) return 'Please select a budget range.';
    }
    return '';
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setStep(prev => Math.min(prev + 1, 4));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    const err = validateStep();
    if (err) { setError(err); return; }

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        access_key: WEB3FORMS_KEY,
        subject: `New Intake: ${formData.businessName} (${clientToken.token})`,
        from_name: formData.contactName,
        email: formData.contactEmail,
        token: clientToken.token,
        businessName: formData.businessName,
        industry: formData.industry,
        website: formData.website,
        contactName: formData.contactName,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        location: formData.location,
        services: formData.services.join(', '),
        projectDescription: formData.projectDescription,
        goals: formData.goals,
        hasLogo: formData.hasLogo,
        brandColors: formData.brandColors,
        brandPersonality: formData.brandPersonality.join(', '),
        competitorSites: formData.competitorSites,
        inspiration: formData.inspiration,
        timeline: formData.timeline,
        budget: formData.budget,
        additionalNotes: formData.additionalNotes,
        uploadedFiles: formData.uploadedFiles.length > 0
          ? formData.uploadedFiles.join('\n')
          : 'No files uploaded',
      };

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (!result.success) throw new Error('Submission failed');
      setSubmitted(true);
    } catch (e) {
      setError('Something went wrong. Please try again or email Create@SiteStackCo.com');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <SuccessScreen clientName={clientToken.clientName} businessName={formData.businessName} />;
  }

  return (
    <div className="min-h-screen bg-dark-900">
      <div className="border-b border-dark-700">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-display text-lg font-semibold text-white">SiteStack</span>
            <span className="font-display text-lg font-semibold text-lime-400">Co</span>
          </div>
          <span className="text-xs text-gray-600 bg-dark-700 border border-dark-500 px-3 py-1 rounded-full">
            Client Onboarding
          </span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        {step === 1 && (
          <div className="mb-8 animate-fade-up">
            <p className="text-xs font-medium text-lime-400 uppercase tracking-widest mb-2">Welcome</p>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white leading-tight mb-3">
              Let's build something great.
            </h1>
            <p className="text-gray-300 text-sm leading-relaxed max-w-lg">
              This intake helps us hit the ground running. It takes about 5 minutes and gives us everything we need to prepare for your kickoff call.
            </p>
            <div className="mt-4 h-px bg-dark-600" />
          </div>
        )}

        <div className="mb-8">
          <ProgressBar currentStep={step} totalSteps={4} stepLabels={STEP_LABELS} />
        </div>

        <div className="mb-8">
          {step === 1 && <Step1BusinessInfo data={formData} onChange={updateForm} />}
          {step === 2 && <Step2Services data={formData} onChange={updateForm} />}
          {step === 3 && <Step3Brand data={formData} onChange={updateForm} />}
          {step === 4 && <Step4Timeline data={formData} onChange={updateForm} />}
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 bg-red-900/20 border border-red-900/40 rounded-lg px-4 py-3">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#f87171" strokeWidth="1.5" strokeLinecap="round">
              <circle cx="7" cy="7" r="6"/><path d="M7 4v3M7 10v.5"/>
            </svg>
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          {step > 1 ? (
            <button
              onClick={handleBack}
              className="px-5 py-2.5 rounded-lg border border-dark-500 text-gray-400 text-sm font-medium hover:border-dark-400 hover:text-gray-300 transition-all duration-200"
            >
              ← Back
            </button>
          ) : (
            <div />
          )}

          {step < 4 ? (
            <button
              onClick={handleNext}
              className="px-6 py-2.5 rounded-lg bg-lime-400 text-dark-900 text-sm font-semibold hover:bg-lime-500 active:scale-95 transition-all duration-200"
            >
              Continue →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-8 py-2.5 rounded-lg bg-lime-400 text-dark-900 text-sm font-semibold hover:bg-lime-500 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3"/>
                    <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                  Submitting...
                </>
              ) : (
                'Submit Intake →'
              )}
            </button>
          )}
        </div>

        <p className="text-xs text-dark-500 text-center mt-8">
          Secured by SiteStackCo · Token-protected · Encrypted in transit
        </p>
      </div>
    </div>
  );
};

export default IntakeForm;
